import { useEffect, useRef, useState } from "react";
import type { PartnerSignatureInput } from "../../shared/types";

interface Props {
  defaultName: string;
  onClose: () => void;
  onAdopt: (sig: PartnerSignatureInput) => void;
}

/**
 * Adopt-and-sign modal: Type or Draw a signature, confirm it is your legal
 * signature, then adopt it. Returns the adopted legal name (which binds the
 * server audit hash) plus, for a drawn signature, the PNG data URL. The server
 * derives the audit (IP + time); this component never sends either.
 *
 * Mounted fresh each time it opens (the parent renders it only while open), so
 * its initial state is correct on every open with no reset effect to race.
 */
export function SignModal({ defaultName, onClose, onAdopt }: Props) {
  const [tab, setTab] = useState<"type" | "draw">("type");
  const [typed, setTyped] = useState(defaultName);
  const [agreed, setAgreed] = useState(false);
  const [drew, setDrew] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);

  useEffect(() => {
    if (tab !== "draw") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const width = canvas.clientWidth || 360;
    canvas.width = width;
    canvas.height = 120;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.strokeStyle = "#12245e";
    ctx.lineWidth = 2.6;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    const pos = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const down = (e: PointerEvent) => {
      drawing.current = true;
      setDrew(true);
      const p = pos(e);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    };
    const move = (e: PointerEvent) => {
      if (!drawing.current) return;
      const p = pos(e);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    };
    const up = () => {
      drawing.current = false;
    };
    canvas.addEventListener("pointerdown", down);
    canvas.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      canvas.removeEventListener("pointerdown", down);
      canvas.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [tab]);

  const hasSignature = tab === "draw" ? drew : typed.trim().length > 0;
  const canAdopt = agreed && hasSignature;

  function clearCanvas() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setDrew(false);
  }

  function adopt() {
    if (!canAdopt) return;
    const name = typed.trim() || defaultName;
    if (tab === "draw") {
      const dataUrl = canvasRef.current?.toDataURL("image/png") ?? "";
      onAdopt({ kind: "drawn", name, dataUrl });
    } else {
      onAdopt({ kind: "typed", name });
    }
  }

  return (
    <div className="scrim open" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="Add your signature">
        <div className="mhead">
          <h3>Add your signature</h3>
          <button className="mx" type="button" onClick={onClose} aria-label="Close">
            &#10005;
          </button>
        </div>
        <div className="sign-tabs">
          <button
            type="button"
            className={tab === "type" ? "on" : ""}
            onClick={() => setTab("type")}
          >
            Type
          </button>
          <button
            type="button"
            className={tab === "draw" ? "on" : ""}
            onClick={() => setTab("draw")}
          >
            Draw
          </button>
        </div>
        <div className="mbody">
          {tab === "type" ? (
            <div>
              <div className="sig-preview">{typed || " "}</div>
              <input
                className="inp"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                aria-label="Typed signature"
              />
            </div>
          ) : (
            <div>
              <canvas ref={canvasRef} id="sigCanvas" aria-label="Draw your signature" />
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                <button className="btn ghost sm" type="button" onClick={clearCanvas}>
                  Clear
                </button>
              </div>
            </div>
          )}
          <label className="agree-check">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            I agree this is my legal signature.
          </label>
        </div>
        <div className="mfoot">
          <button className="btn ghost" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="btn" type="button" disabled={!canAdopt} onClick={adopt}>
            Adopt and sign
          </button>
        </div>
      </div>
    </div>
  );
}
